/**
 * Proximity batching for the SEG-01 route leg — turn a segment's audience
 * (e.g. "fall furnace checks due in Eagan/Apple Valley/Burnsville") into
 * compact, schedulable day-batches:
 *
 *   1. Sweep-cluster: seed each batch with the point farthest from the
 *      remaining audience's center (work the edges inward — the classic sweep
 *      heuristic), then grow it with the nearest remaining points until the
 *      calls-per-day cap. Compact clusters, explainable ("these 8 are
 *      neighbors"), hard size cap (a day's capacity).
 *   2. Order each batch with the same window-aware optimizer the Board uses
 *      (no windows here yet → pure geography) and price it in miles/minutes.
 *
 * Pure functions, no fetching — the route-leg view feeds it points.
 * PROPOSES ONLY; booking stays with the dispatcher (DEC-25: never writes ESC).
 */
import { routeMiles, optimizeBanded, driveMinutes } from "@/lib/route";

export interface BatchPoint {
  key: string; // custno|locno
  lat: number;
  lng: number;
  label: string; // customer name
  city: string | null;
}

export interface Batch<T extends BatchPoint> {
  index: number; // 1-based
  stops: T[]; // in suggested drive order
  miles: number;
  minutes: number;
  cities: string[]; // dominant cities (up to 2), for the batch label
}

// Equirectangular miles — plenty accurate at metro scale, cheap enough to run
// inside the clustering loops. (Exported for the route-leg's region math.)
export function milesBetween(
  a: { lat: number; lng: number },
  b: { lat: number; lng: number },
): number {
  const dy = (a.lat - b.lat) * 69.17;
  const dx =
    (a.lng - b.lng) * 69.17 * Math.cos(((a.lat + b.lat) / 2) * (Math.PI / 180));
  return Math.sqrt(dx * dx + dy * dy);
}

export function centroid(pts: { lat: number; lng: number }[]): {
  lat: number;
  lng: number;
} {
  let lat = 0;
  let lng = 0;
  for (const p of pts) {
    lat += p.lat;
    lng += p.lng;
  }
  return { lat: lat / pts.length, lng: lng / pts.length };
}

/** Top (up to) two city names in a batch, by count — for the batch label. */
function dominantCities(stops: { city: string | null }[]): string[] {
  const counts = new Map<string, number>();
  for (const s of stops) {
    const c = (s.city ?? "").trim();
    if (!c) continue;
    counts.set(c, (counts.get(c) ?? 0) + 1);
  }
  return [...counts.entries()]
    .sort((a, b) => b[1] - a[1])
    .slice(0, 2)
    .map(([c]) => c);
}

/**
 * Cluster `points` into batches of at most `perBatch`, each ordered into a
 * suggested drive sequence with its straight-line(×1.3) miles + minutes.
 */
export function batchByProximity<T extends BatchPoint>(
  points: T[],
  perBatch: number,
): Batch<T>[] {
  if (points.length === 0 || perBatch < 1) return [];
  const remaining = [...points];
  const clusters: T[][] = [];

  while (remaining.length > 0) {
    // Seed: the point farthest from the center of what's left.
    const c = centroid(remaining);
    let seedIdx = 0;
    let seedDist = -1;
    for (let i = 0; i < remaining.length; i++) {
      const d = milesBetween(remaining[i], c);
      if (d > seedDist) {
        seedDist = d;
        seedIdx = i;
      }
    }
    const batch: T[] = [remaining.splice(seedIdx, 1)[0]];

    // Grow: nearest remaining point to the batch's running centroid.
    while (batch.length < perBatch && remaining.length > 0) {
      const bc = centroid(batch);
      let bestIdx = 0;
      let bestDist = Infinity;
      for (let i = 0; i < remaining.length; i++) {
        const d = milesBetween(remaining[i], bc);
        if (d < bestDist) {
          bestDist = d;
          bestIdx = i;
        }
      }
      batch.push(remaining.splice(bestIdx, 1)[0]);
    }
    clusters.push(batch);
  }

  return clusters.map((stops, i) => {
    // One band → the optimizer is pure nearest-neighbor + 2-opt geography.
    const ordered = optimizeBanded(
      stops.map((s) => ({ ...s, band: 0 })),
    ) as unknown as T[];
    const miles = routeMiles(ordered);
    return {
      index: i + 1,
      stops: ordered,
      miles,
      minutes: driveMinutes(miles),
      cities: dominantCities(ordered),
    };
  });
}
